import { useEffect, useMemo } from 'react';

import { usePageAdf } from '@confluence/connectors/entry-points/usePageAdf.ts';
import {
	type DevDocsBylineConnectionAncestor,
	type DevDocsBylineConnection,
	useDevDocsBylineConnection,
} from '@confluence/dev-docs/entry-points/DevDocsBylineConnection.ts';

import { useAtAGlance, type UseAtAGlanceResult } from '../at-a-glance/useAtAGlance.ts';
import { clearAtAGlance, publishAtAGlance } from '../store/atAGlanceBridge.ts';
import {
	clearDevDocsConnection,
	publishDevDocsConnection,
} from '../store/devDocsConnectionBridge.ts';
import { clearIncomingLinks, publishIncomingLinks } from '../store/incomingLinksBridge.ts';
import { clearOutgoingLinks, publishOutgoingLinks } from '../store/outgoingLinksBridge.ts';
import { clearRelatedContent, publishRelatedContent } from '../store/relatedContentBridge.ts';

import { useIncomingLinks, type UseIncomingLinksResult } from './useIncomingLinks.ts';
import { useRelatedContent, type UseRelatedContentResult } from './useRelatedContent.ts';
import { useSmartLinks, type UseSmartLinksResult } from './useSmartLinks.ts';

type BylineConnectionsDataOptions = {
	contentId?: string;
	connectionAncestors?: readonly DevDocsBylineConnectionAncestor[] | null;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageTitle?: string | null;
};

type BylineConnectionsData = {
	outgoing: UseSmartLinksResult;
	incoming: UseIncomingLinksResult;
	relatedContent: UseRelatedContentResult;
	devDocsConnection: DevDocsBylineConnection | null;
	atAGlance: UseAtAGlanceResult;
};

/** Sole fetching owner, mounted by the byline under its SmartCard provider. */
export const useBylineConnectionsData = ({
	contentId,
	connectionAncestors,
	pageVersion,
	pageUpdatedAt,
	pageTitle,
}: BylineConnectionsDataOptions): BylineConnectionsData => {
	const { adf: pageAdf, contentId: pageAdfContentId } = usePageAdf(contentId);
	const outgoing = useSmartLinks(contentId);
	const {
		links: incomingLinks,
		loading,
		loadingMore,
		hasNextPage,
		endCursor,
		loadMore,
		error,
	} = useIncomingLinks(contentId);
	// Keep bridge snapshots stable when unrelated byline or AI state changes.
	const incoming = useMemo(
		() => ({ links: incomingLinks, loading, loadingMore, hasNextPage, endCursor, loadMore, error }),
		[incomingLinks, loading, loadingMore, hasNextPage, endCursor, loadMore, error],
	);
	const {
		enabled,
		links: relatedLinks,
		loading: relatedLoading,
		error: relatedError,
	} = useRelatedContent(contentId);
	const relatedContent = useMemo(
		() => ({ enabled, links: relatedLinks, loading: relatedLoading, error: relatedError }),
		[enabled, relatedLinks, relatedLoading, relatedError],
	);
	const devDocsConnection = useDevDocsBylineConnection(connectionAncestors);
	const atAGlance = useAtAGlance({
		contentId,
		pageTitle,
		pageAdf:
			pageAdfContentId === contentId && outgoing.collectedForContentId === contentId
				? pageAdf
				: null,
		pageVersion,
		pageUpdatedAt,
		structuralAncestors: connectionAncestors,
		outgoingLinksLoading: outgoing.loading,
		// useAtAGlance also waits for incoming.loading via appearsIn. Related
		// content is sent when it is ready and does not block the first request.
		connections: {
			appearsIn: incoming,
			linksTo: { links: outgoing.links },
			relatedContent,
		},
	});

	// Publish the collected links and page metadata so the Teamwork Graph preview
	// panel can use them: it mounts outside this subtree (see the bridge's own doc
	// comment), so props and context cannot reach it. Republishing on every change
	// is what keeps a row the Connect flow just upgraded in sync inside the panel.
	//
	// Tagged with `collectedForContentId`, not `contentId`: during an SPA transition
	// `contentId` updates before the new page's links are collected, so publishing
	// the current id would label page A's links as page B's and the reader's content
	// check would wave them through. The version and update time are included only
	// once their props belong to that same collected content id for the same reason.
	useEffect(() => {
		publishOutgoingLinks({
			contentId: outgoing.collectedForContentId,
			pageAdf: pageAdfContentId === outgoing.collectedForContentId ? pageAdf : null,
			pageVersion: outgoing.collectedForContentId === contentId ? pageVersion : undefined,
			pageUpdatedAt: outgoing.collectedForContentId === contentId ? pageUpdatedAt : undefined,
			structuralAncestors:
				outgoing.collectedForContentId === contentId ? connectionAncestors : undefined,
			outgoingLinks: outgoing.links,
			refreshLinkUrl: outgoing.refreshLinkUrl,
		});
	}, [
		outgoing.collectedForContentId,
		connectionAncestors,
		contentId,
		outgoing.links,
		pageAdf,
		pageAdfContentId,
		pageUpdatedAt,
		pageVersion,
		outgoing.refreshLinkUrl,
	]);

	// Separate from the publish effect so the store is only emptied when the
	// byline actually goes away, not on every republish.
	useEffect(() => clearOutgoingLinks, []);

	// Tagged with `contentId` rather than a collected-for id because
	// `connectionAncestors` arrives as a prop alongside it, so the two move together.
	useEffect(() => {
		publishDevDocsConnection({ contentId, devDocsConnection });
	}, [contentId, devDocsConnection]);

	useEffect(() => clearDevDocsConnection, []);

	useEffect(() => {
		publishIncomingLinks({ contentId, incomingLinks: incoming });
	}, [contentId, incoming]);
	useEffect(() => clearIncomingLinks, []);

	useEffect(() => {
		publishRelatedContent({ contentId, relatedContent });
	}, [contentId, relatedContent]);
	useEffect(() => clearRelatedContent, []);

	useEffect(() => {
		publishAtAGlance({ contentId, atAGlance });
	}, [contentId, atAGlance]);
	useEffect(() => clearAtAGlance, []);

	return { outgoing, incoming, relatedContent, devDocsConnection, atAGlance };
};
